import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { gamePlayService } from '../../services/db';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../context/ToastContext';
import Spinner from '../../components/ui/Spinner';

const shuffleArray = (arr) => {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const formatTime = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m < 10 ? '0' + m : m}:${s < 10 ? '0' + s : s}`;
};

const GamePlayerPage = () => {
  const { seasonId, quizId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();

  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [timeRemaining, setTimeRemaining] = useState(300);
  const [timeTaken, setTimeTaken] = useState(0);
  const [score, setScore] = useState(0);
  const [userResponses, setUserResponses] = useState({});
  const [shuffledOptions, setShuffledOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);

  const [showExitModal, setShowExitModal] = useState(false);

  // References for mutable timer state
  const timeRemainingRef = useRef(timeRemaining);
  const timeTakenRef = useRef(timeTaken);
  const scoreRef = useRef(score);
  const userResponsesRef = useRef(userResponses);
  const submittingRef = useRef(false);

  // Toast notification throttle for blocked copy attempts
  const lastToastTimeRef = useRef(0);
  const notifyCopyBlocked = useCallback(() => {
    const now = Date.now();
    if (now - lastToastTimeRef.current > 2500) {
      lastToastTimeRef.current = now;
      showToast('Copying question content is disabled to encourage genuine competition 🛡️', 'warning');
    }
  }, [showToast]);

  // Anti-Copying & Anti-Cheating Event Listeners for genuine competition
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isCtrlOrCmd = e.ctrlKey || e.metaKey;

      // Prevent Ctrl+C / Cmd+C (Copy), Ctrl+A / Cmd+A (Select All), Ctrl+X (Cut), Ctrl+U (View Source), Ctrl+P (Print), Ctrl+S (Save)
      if (
        isCtrlOrCmd &&
        ['c', 'C', 'a', 'A', 'x', 'X', 'u', 'U', 'p', 'P', 's', 'S'].includes(e.key)
      ) {
        e.preventDefault();
        e.stopPropagation();
        notifyCopyBlocked();
        return false;
      }

      // Prevent DevTools inspection shortcuts (F12, Ctrl+Shift+I, Cmd+Opt+I, etc.)
      if (
        e.key === 'F12' ||
        (isCtrlOrCmd && (e.shiftKey || e.altKey) && ['I', 'i', 'J', 'j', 'C', 'c'].includes(e.key))
      ) {
        e.preventDefault();
        e.stopPropagation();
        notifyCopyBlocked();
        return false;
      }
    };

    const handleCopyCut = (e) => {
      e.preventDefault();
      if (e.clipboardData) {
        e.clipboardData.clearData();
      }
      if (window.getSelection) {
        window.getSelection().removeAllRanges();
      }
      notifyCopyBlocked();
    };

    const handleContextMenu = (e) => {
      e.preventDefault();
      notifyCopyBlocked();
    };

    const handleSelectStart = (e) => {
      e.preventDefault();
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('copy', handleCopyCut, true);
    window.addEventListener('cut', handleCopyCut, true);
    window.addEventListener('contextmenu', handleContextMenu, true);
    window.addEventListener('selectstart', handleSelectStart, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('copy', handleCopyCut, true);
      window.removeEventListener('cut', handleCopyCut, true);
      window.removeEventListener('contextmenu', handleContextMenu, true);
      window.removeEventListener('selectstart', handleSelectStart, true);
    };
  }, [notifyCopyBlocked]);

  timeRemainingRef.current = timeRemaining;
  timeTakenRef.current = timeTaken;
  scoreRef.current = score;
  userResponsesRef.current = userResponses;

  const handleFinishGame = useCallback(async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);

    const finalScore = scoreRef.current;
    const finalTimeTaken = timeTakenRef.current;
    const finalResponses = userResponsesRef.current;
    const totalQuestions = questions.length;

    try {
      if (user) {
        await gamePlayService.submitQuizResult({
          seasonId,
          quizId,
          quizTitle: quiz?.title || 'Bible Contest',
          user,
          score: finalScore,
          timeTaken: finalTimeTaken,
          numQuestions: totalQuestions,
          userResponses: finalResponses,
        });
      }
    } catch (err) {
      console.error('Failed to submit results:', err);
    }

    // Navigate to results screen with game summary
    navigate(`/play/season/${seasonId}/quiz/${quizId}/results`, {
      replace: true,
      state: {
        score: finalScore,
        timeTaken: finalTimeTaken,
        numQuestions: totalQuestions,
        userResponses: finalResponses,
        questions,
        quizTitle: quiz?.title,
      },
    });
  }, [seasonId, quizId, user, questions, quiz, navigate]);

  // Load Quiz & Questions
  useEffect(() => {
    const loadGame = async () => {
      setLoading(true);
      try {
        const [quizData, questionList] = await Promise.all([
          gamePlayService.getQuizDetails(seasonId, quizId),
          gamePlayService.getQuestions(seasonId, quizId),
        ]);

        if (!quizData || !questionList || questionList.length === 0) {
          setLoading(false);
          return;
        }

        setQuiz(quizData);
        const randomizedQuestions = shuffleArray(questionList);
        setQuestions(randomizedQuestions);

        const durationSeconds = (Number(quizData.duration) || 5) * 60;
        setTimeRemaining(durationSeconds);
        timeRemainingRef.current = durationSeconds;

        // Shuffle options for first question
        if (randomizedQuestions[0]) {
          const q0 = randomizedQuestions[0];
          const rawOpts = [q0.option_1, q0.option_2, q0.option_3, q0.option_4].filter(Boolean);
          setShuffledOptions(shuffleArray(rawOpts));
        }
      } catch (err) {
        console.error('Failed to initialize game:', err);
      } finally {
        setLoading(false);
      }
    };

    loadGame();
  }, [seasonId, quizId]);

  // Game Timer Hook
  useEffect(() => {
    if (loading || submitting || questions.length === 0) return;

    const timerInterval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timerInterval);
          handleFinishGame();
          return 0;
        }
        return prev - 1;
      });
      setTimeTaken((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [loading, submitting, questions.length, handleFinishGame]);

  // Handle Option Selection
  const handleSelectOption = (optionText) => {
    if (selectedOption !== null || submitting) return;

    setSelectedOption(optionText);

    const currentQuestion = questions[currentIndex];
    const qId = currentQuestion.question_id || currentQuestion.id || `q_${currentIndex}`;
    const isCorrect = String(optionText).trim().toLowerCase() === String(currentQuestion.answer).trim().toLowerCase();

    // Update responses
    const nextResponses = { ...userResponsesRef.current, [qId]: optionText };
    setUserResponses(nextResponses);
    userResponsesRef.current = nextResponses;

    if (isCorrect) {
      setScore((prev) => prev + 1);
      scoreRef.current += 1;
    }

    // Advance to next question after brief 250ms tap animation
    setTimeout(() => {
      setSelectedOption(null);
      const nextIndex = currentIndex + 1;

      if (nextIndex < questions.length) {
        setCurrentIndex(nextIndex);
        const nextQ = questions[nextIndex];
        const rawOpts = [nextQ.option_1, nextQ.option_2, nextQ.option_3, nextQ.option_4].filter(Boolean);
        setShuffledOptions(shuffleArray(rawOpts));
      } else {
        handleFinishGame();
      }
    }, 250);
  };

  if (loading || submitting) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '75vh', gap: '16px' }}>
        <Spinner size="lg" />
        <p style={{ color: 'var(--clr-muted)', fontSize: '14px', fontWeight: 600 }}>
          {submitting ? 'Submitting your contest score...' : 'Preparing Scripture questions...'}
        </p>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div style={{ maxWidth: '600px', margin: '60px auto', textAlign: 'center', padding: '20px' }}>
        <span style={{ fontSize: '48px', display: 'block', marginBottom: '12px' }}>📖</span>
        <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px', color: 'var(--clr-text-1)' }}>No Questions in Quiz</h2>
        <p style={{ color: 'var(--clr-muted)', marginBottom: '20px', fontSize: '14.5px' }}>
          There are no questions in the quiz, please reachout to season host.
        </p>
        <button
          type="button"
          onClick={() => navigate(`/play/season/${seasonId}`)}
          style={{ padding: '10px 20px', background: 'var(--clr-primary)', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 700 }}
        >
          Return to Season
        </button>
      </div>
    );
  }

  const currentQ = questions[currentIndex];
  const progressPercent = ((currentIndex + 1) / questions.length) * 100;
  const isTimeLow = timeRemaining < 30;

  return (
    <div
      className="game-player-protected"
      onCopy={(e) => { e.preventDefault(); notifyCopyBlocked(); }}
      onCut={(e) => { e.preventDefault(); notifyCopyBlocked(); }}
      onPaste={(e) => e.preventDefault()}
      onContextMenu={(e) => { e.preventDefault(); notifyCopyBlocked(); }}
      onDragStart={(e) => e.preventDefault()}
      onSelectStart={(e) => e.preventDefault()}
      style={{
        maxWidth: '720px',
        margin: '0 auto',
        padding: 'var(--sp-4) var(--sp-4) var(--sp-8) var(--sp-4)',
        minHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        WebkitTouchCallout: 'none',
      }}
    >
      {/* Top Header Bar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <button
          type="button"
          onClick={() => setShowExitModal(true)}
          style={{
            background: 'var(--clr-background)',
            border: '1px solid var(--clr-border)',
            borderRadius: '10px',
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: 700,
            color: 'var(--clr-muted)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
          }}
        >
          ✕ Exit
        </button>

        <div style={{ fontWeight: 800, fontSize: '15px', color: 'var(--clr-text-1)', letterSpacing: '0.02em', textAlign: 'center' }}>
          {quiz?.title || 'BIBLE CONTEST'}
        </div>

        {/* Live Timer Pill */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 14px',
            borderRadius: '9999px',
            background: isTimeLow ? '#FEF2F2' : '#FFF4F3',
            border: isTimeLow ? '1.5px solid #EF4444' : '1px solid var(--clr-primary)',
            color: isTimeLow ? '#DC2626' : 'var(--clr-primary)',
            fontWeight: 800,
            fontSize: '13.5px',
            boxShadow: isTimeLow ? '0 0 10px rgba(239, 68, 68, 0.3)' : 'none',
            transition: 'all 0.3s ease',
          }}
        >
          <span>⏱️</span>
          <span>{formatTime(timeRemaining)}</span>
        </div>
      </div>

      {/* Progress Bar & Question Count */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', fontWeight: 700, color: 'var(--clr-muted)', marginBottom: '8px' }}>
          <span>Question {currentIndex + 1} of {questions.length}</span>
        </div>

        <div
          style={{
            width: '100%',
            height: '8px',
            background: 'var(--clr-background)',
            borderRadius: '9999px',
            overflow: 'hidden',
            border: '1px solid var(--clr-border)',
          }}
        >
          <div
            style={{
              width: `${progressPercent}%`,
              height: '100%',
              background: 'linear-gradient(90deg, #FF5C56 0%, #FF8A85 100%)',
              borderRadius: '9999px',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </div>

      {/* Question Card */}
      <div
        className="card"
        style={{
          background: 'var(--clr-surface)',
          border: '1px solid var(--clr-border)',
          borderRadius: '24px',
          padding: 'clamp(20px, 4vw, 32px)',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.05)',
          marginBottom: '20px',
        }}
      >
        {currentQ.verse && (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 10px',
              borderRadius: '6px',
              background: 'var(--clr-primary-subtle, #FFF4F3)',
              color: 'var(--clr-primary, #FF5C56)',
              fontSize: '12px',
              fontWeight: 700,
              marginBottom: '12px',
            }}
          >
            <span>📖</span> {currentQ.verse}
          </div>
        )}

        <h2
          style={{
            fontSize: 'clamp(17px, 3.5vw, 22px)',
            fontWeight: 800,
            lineHeight: 1.4,
            margin: 0,
            color: 'var(--clr-text-1)',
          }}
        >
          {currentQ.question}
        </h2>
      </div>

      {/* Options Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: '12px',
          marginBottom: '20px',
        }}
      >
        {shuffledOptions.map((optionText, idx) => {
          const letter = String.fromCharCode(65 + idx); // A, B, C, D
          const isSelected = selectedOption === optionText;

          return (
            <button
              key={`${currentIndex}_${idx}`}
              type="button"
              disabled={selectedOption !== null}
              onClick={() => handleSelectOption(optionText)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                padding: '16px 20px',
                borderRadius: '16px',
                border: isSelected ? '2px solid var(--clr-primary)' : '1px solid var(--clr-border)',
                background: isSelected ? 'var(--clr-primary-subtle, #FFF4F3)' : 'var(--clr-surface)',
                color: 'var(--clr-text-1)',
                fontSize: '15px',
                fontWeight: 600,
                textAlign: 'left',
                cursor: selectedOption !== null ? 'default' : 'pointer',
                transform: isSelected ? 'scale(0.98)' : 'none',
                transition: 'all 0.15s ease',
                boxShadow: isSelected ? '0 4px 14px rgba(255, 92, 86, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.02)',
              }}
            >
              <span
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: isSelected ? 'var(--clr-primary)' : 'var(--clr-background)',
                  color: isSelected ? '#FFFFFF' : 'var(--clr-muted)',
                  border: isSelected ? 'none' : '1px solid var(--clr-border)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  fontSize: '13px',
                  flexShrink: 0,
                }}
              >
                {letter}
              </span>
              <span style={{ flex: 1, lineHeight: 1.35 }}>{optionText}</span>
            </button>
          );
        })}
      </div>

      {/* Exit Confirmation Modal */}
      {showExitModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.65)',
            backdropFilter: 'blur(3px)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            style={{
              background: 'var(--clr-surface)',
              borderRadius: '20px',
              padding: '24px',
              maxWidth: '400px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
            }}
          >
            <div style={{ fontSize: '36px', marginBottom: '8px' }}>⚠️</div>
            <h3 style={{ fontSize: '18px', fontWeight: 800, margin: '0 0 8px 0', color: 'var(--clr-text-1)' }}>
              Leave Quiz in Progress?
            </h3>
            <p style={{ fontSize: '13.5px', color: 'var(--clr-muted)', margin: '0 0 20px 0', lineHeight: 1.4 }}>
              If you exit now, your current score will be submitted as-is. Keep going and finish strong!
            </p>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setShowExitModal(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '12px',
                  border: 'none',
                  background: 'var(--clr-primary)',
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: '13.5px',
                  cursor: 'pointer',
                }}
              >
                Keep Playing
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowExitModal(false);
                  handleFinishGame();
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '12px',
                  border: '1px solid var(--clr-border)',
                  background: 'transparent',
                  color: '#DC2626',
                  fontWeight: 700,
                  fontSize: '13.5px',
                  cursor: 'pointer',
                }}
              >
                Exit & Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GamePlayerPage;
